import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableNode,
  getDOMCellFromTarget,
  TableCellNode,
} from '@lexical/table';
import { Dropdown, MenuProps } from 'antd';
import {
  $findMatchingParent,
  $getNearestNodeFromDOMNode,
  $getSelection,
  $isRangeSelection,
} from 'lexical';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const BUTTON_WIDTH = 16;
const CELL_PADDING = 6;

function TableCellActionMenu({
  editor,
  tableCellNode,
  onClose,
  onMenuOpenChange,
}: {
  editor: any;
  tableCellNode: TableCellNode;
  onClose: () => void;
  onMenuOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('common');
  const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
  const [cellElement, setCellElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = editor.getElementByKey(tableCellNode.getKey());
    if (el) {
      setCellElement(el);
      const { top, right } = el.getBoundingClientRect();
      setCoords({
        x: right - BUTTON_WIDTH - CELL_PADDING + window.scrollX,
        y: top + CELL_PADDING + window.scrollY,
      });
    }
  }, [editor, tableCellNode]);

  const runAction = (action: () => void) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.anchor.set(tableCellNode.getKey(), 0, 'element');
        selection.focus.set(tableCellNode.getKey(), 0, 'element');
        action();
      }
    });
  };

  const handleMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'insert_row_above':
        runAction(() => {
          $insertTableRowAtSelection(false);
        });
        break;
      case 'insert_row_below':
        runAction(() => {
          $insertTableRowAtSelection(true);
        });
        break;
      case 'insert_col_left':
        runAction(() => {
          $insertTableColumnAtSelection(false);
        });
        break;
      case 'insert_col_right':
        runAction(() => {
          $insertTableColumnAtSelection(true);
        });
        break;
      case 'delete_row':
        runAction($deleteTableRowAtSelection);
        break;
      case 'delete_col':
        runAction($deleteTableColumnAtSelection);
        break;
      case 'delete_table':
        runAction(() => {
          const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          tableNode.remove();
        });
        break;
      default:
        break;
    }
  };

  const items: MenuProps['items'] = [
    {
      key: 'insert_row_above',
      label: t('insert_row_above'),
    },
    {
      key: 'insert_row_below',
      label: t('insert_row_below'),
    },
    {
      type: 'divider',
    },
    {
      key: 'insert_col_left',
      label: t('insert_column_left'),
    },
    {
      key: 'insert_col_right',
      label: t('insert_column_right'),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete_row',
      label: t('delete_row'),
    },
    {
      key: 'delete_col',
      label: t('delete_column'),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete_table',
      label: t('delete_table'),
    },
  ];

  if (!coords || !cellElement) return null;

  return createPortal(
    <Dropdown
      menu={{
        items,
        onClick: handleMenuClick,
      }}
      trigger={['click']}
      onOpenChange={(open) => {
        onMenuOpenChange(open);
        if (!open) {
          onClose();
        }
      }}
    >
      <button
        className="table-cell-action-button"
        style={{
          position: 'absolute',
          top: coords.y,
          left: coords.x,
          zIndex: 1001,
        }}
        aria-haspopup="menu"
      >
        <i className="chevron-down"></i>
      </button>
    </Dropdown>,
    document.body
  );
}

export default function TableActionMenuPlugin(): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const isEditable = useLexicalEditable();
  const [hoveredCell, setHoveredCell] = useState<TableCellNode | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const onCellClick = (event: MouseEvent) => {
      if (isMenuOpen) return;
      const target = event.target as HTMLElement;

      let selectionInsideTable = false;
      editor.read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          try {
            const anchorNode = selection.anchor.getNode();
            const table = $findMatchingParent(anchorNode, (node) =>
              $isTableNode(node)
            );
            if (table) selectionInsideTable = true;
          } catch (e) {
            // ignore errors when selection points to removed nodes
          }
        }
      });

      if (!selectionInsideTable) {
        setHoveredCell(null);
        return;
      }

      const editorElement = editor.getRootElement();
      if (editorElement && !editorElement.contains(document.activeElement)) {
        setHoveredCell(null);
        return;
      }

      editor.read(() => {
        const domCell = getDOMCellFromTarget(target);
        if (domCell) {
          const cellNode = $getNearestNodeFromDOMNode(domCell.elem);
          if (cellNode && $isTableCellNode(cellNode)) {
            setHoveredCell(cellNode);
            return;
          }
        }
        if (!target.closest('.table-cell-action-button')) {
          setHoveredCell(null);
        }
      });
    };

    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener('click', onCellClick);
    }
    return () => {
      if (editorElement) {
        editorElement.removeEventListener('click', onCellClick);
      }
    };
  }, [editor, isMenuOpen]);

  if (!isEditable || !hoveredCell) {
    return null;
  }

  return (
    <TableCellActionMenu
      editor={editor}
      tableCellNode={hoveredCell}
      onClose={() => setHoveredCell(null)}
      onMenuOpenChange={setIsMenuOpen}
    />
  );
}
